import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ExportButtons from './components/ExportButtons';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import FiltersBar from './components/FiltersBar';
import HelpButton from '../../help/HelpButton';

function fmtSecs(n) {
  if (n == null) return '—';
  const s = Number(n) || 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// CX_INTERACTION_ID Phase D: per-agent handle time from interaction SEGMENTS
// (the CX-owned model). Transfer-accurate — each owner's handling window is a
// segment, so a transferred call splits correctly across agents (unlike the
// CDR src/dst heuristic used by the classic Agent Productivity report).
export default function AgentByInteraction() {
  const [range, setRange, apiParams] = useRangeState('today');
  const [filters, setFilters] = useState({});
  const merged = { ...apiParams, ...filters };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'agents-by-interaction', merged],
    queryFn: () => reportsApi.agentsByInteraction(merged),
    refetchInterval: range.preset === 'today' ? 30_000 : false,
    keepPreviousData: true,
  });

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar value={filters} onChange={setFilters} show={['agent']} />

      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-base font-semibold">Agent handle time (by interaction)</h2>
          <p className="text-xs text-surface-muted">
            Transfer-accurate per-owner handling windows from the CX interaction model.
            AHT = average handling window; transfers split across each owning agent.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton moduleKey="reports" />
          <ExportButtons report="agents-by-interaction" params={merged} disabled={isLoading} />
          <button
            type="button"
            onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
          >
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-600 dark:text-red-300 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      <ReportTable
        headers={['extension','agent_name','segments','transfer_segments','total_talk_sec','avg_handle_sec','last_at']}
        rows={data?.rows || []}
        columnLabels={{
          extension: 'Ext',
          agent_name: 'Agent',
          segments: 'Handled',
          transfer_segments: 'Via transfer',
          total_talk_sec: 'Total talk',
          avg_handle_sec: 'AHT',
          last_at: 'Last',
        }}
        formatters={{
          total_talk_sec: (v) => fmtSecs(v),
          avg_handle_sec: (v) => fmtSecs(v),
          last_at: (v) => (v ? String(v).replace('T', ' ').replace(/\.\d+.*$/, '') : '—'),
        }}
      />

      {!isLoading && (data?.rows || []).length === 0 && (
        <div className="text-sm text-surface-muted mt-3">
          No interaction segments in this window yet.
        </div>
      )}
    </div>
  );
}
